import React from 'react';
import { Card, Row, Col, Typography, Tooltip, Divider } from 'antd';
import { InfoCircleOutlined, CameraOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Text, Title } = Typography;

/**
 * Component to display vitals snapshot data captured at encounter save time
 * This handles the different data structure of vitalsSnapshot vs regular vitals
 * Only displays actual vital signs (excludes biometrics, labs, and other parameters)
 */
const VitalsSnapshotDisplay = ({ vitalsSnapshot, signedAt, source }) => {


  if (!vitalsSnapshot || Object.keys(vitalsSnapshot).length === 0) {
    console.log('DEBUG VitalsSnapshotDisplay: No vitals snapshot data - returning empty message');
    return <Text type="secondary">No vitals snapshot available.</Text>;
  }

  // Helper function to extract value from snapshot entry
  const getSnapshotValue = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'object' && entry.value !== undefined) {
      return entry.value;
    }
    return entry;
  };

  // Helper function to get units from snapshot entry
  const getSnapshotUnits = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'object' && entry.units) {
      return entry.units;
    }
    return '';
  };

  // Helper function to get timestamp from snapshot entry
  const getSnapshotTimestamp = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'object' && entry.timestamp) {
      return entry.timestamp;
    }
    return null;
  };

  // Helper function to get source from snapshot entry
  const getSnapshotSource = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'object' && entry.source) {
      return entry.source;
    }
    return '';
  };

  // Format vital sign for display with user-friendly labels
  const formatSnapshotVital = (label, entry, defaultUnit = '') => {
    let value = getSnapshotValue(entry);
    const units = getSnapshotUnits(entry) || defaultUnit;
    const timestamp = getSnapshotTimestamp(entry);

    if (value === null || value === undefined) {
      return null;
    }

    // Convert temperature to Celsius if it appears to be in Fahrenheit
    if (label === 'Temperature' && units === '°C') {
      const tempValue = parseFloat(value);
      if (!isNaN(tempValue)) {
        // If temperature is > 50, assume it's in Fahrenheit and convert to Celsius
        if (tempValue > 50) {
          value = ((tempValue - 32) * 5/9).toFixed(1);
        } else {
          // Already in Celsius, just format to 1 decimal place
          value = tempValue.toFixed(1);
        }
      }
    }

    return (
      <div style={{ marginBottom: '8px', padding: '6px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
        <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>{label}</Text>
        <br />
        <Text style={{ fontSize: '13px', fontWeight: '500' }}>
          {value}{units ? ` ${units}` : ''}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: '10px' }}>
          {timestamp ? moment(timestamp).format('MMM D, YYYY h:mm A') : 'No timestamp'}
        </Text>
      </div>
    );
  };

  // Get user-friendly vital signs with proper labels and ordering
  const getFormattedVitals = () => {
    const vitalMappings = {
      // Blood Pressure (combine systolic and diastolic)
      bp: {
        label: 'Blood Pressure',
        format: (sbp, dbp) => sbp && dbp ? `${sbp}/${dbp}` : (sbp || dbp || 'N/A'),
        unit: 'mmHg'
      },
      // Individual vitals
      pulse: {
        keys: ['vitals_rhr', 'heartrate'],
        label: 'Pulse',
        unit: 'bpm'
      },
      respiratory: {
        keys: ['vitals_rr', 'respiratoryrate'],
        label: 'Respiratory Rate',
        unit: 'breaths/min'
      },
      spo2: {
        keys: ['vitals_spo2', 'oxygensaturation'],
        label: 'SpO₂',
        unit: '%'
      },
      temperature: {
        keys: ['vitals_temp_c', 'temperature'],
        label: 'Temperature',
        unit: '°C'
      },
      weight: {
        keys: ['vitals_weight', 'weight'],
        label: 'Weight',
        unit: 'kg'
      },
      height: {
        keys: ['vitals_height', 'height'],
        label: 'Height',
        unit: 'cm'
      },
      bmi: {
        keys: ['vitals_bmi', 'bmi'],
        label: 'BMI',
        unit: 'kg/m²'
      },
      pain: {
        keys: ['vitals_pain', 'pain'],
        label: 'Pain Level',
        unit: '/10'
      }
    };

    const formattedVitals = [];

    // Process each vital type
    Object.entries(vitalMappings).forEach(([vitalType, config]) => {
      if (vitalType === 'bp') {
        // Special handling for blood pressure - find SBP and DBP separately
        const sbpKeys = ['vitals_sbp', 'bloodpressuresystolic'];
        const dbpKeys = ['vitals_dbp', 'bloodpressurediastolic'];

        const sbpKey = sbpKeys.find(key => vitalsSnapshot[key]);
        const dbpKey = dbpKeys.find(key => vitalsSnapshot[key]);

        if (sbpKey || dbpKey) {
          const sbpValue = sbpKey ? getSnapshotValue(vitalsSnapshot[sbpKey]) : null;
          const dbpValue = dbpKey ? getSnapshotValue(vitalsSnapshot[dbpKey]) : null;
          const timestamp = sbpKey ? getSnapshotTimestamp(vitalsSnapshot[sbpKey]) :
                           (dbpKey ? getSnapshotTimestamp(vitalsSnapshot[dbpKey]) : null);

          formattedVitals.push({
            label: config.label,
            value: config.format(sbpValue, dbpValue),
            unit: config.unit,
            timestamp: timestamp
          });
        }
      } else {
        // Regular vitals
        const foundKey = config.keys.find(key => vitalsSnapshot[key]);
        if (foundKey) {
          const entry = vitalsSnapshot[foundKey];
          formattedVitals.push({
            label: config.label,
            value: getSnapshotValue(entry),
            unit: getSnapshotUnits(entry) || config.unit,
            timestamp: getSnapshotTimestamp(entry)
          });
        }
      }
    });

    return formattedVitals;
  };

  const formattedVitals = getFormattedVitals();



  const cardTitle = (
    <Row justify="space-between" align="middle">
      <Col>
        <Text strong style={{ margin: 0, color: '#52c41a', fontSize: '13px' }}>
          <CameraOutlined style={{ marginRight: 6, fontSize: '12px' }} />
          Vitals Snapshot
        </Text>
      </Col>
      <Col>
        <Tooltip title={`Vitals captured at signing time: ${signedAt ? moment(signedAt).format('M/D/YYYY HH:mm') : 'Unknown'}`}>
          <InfoCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
        </Tooltip>
      </Col>
    </Row>
  );



  return (
    <Card
      size="small"
      title={cardTitle}
      style={{
        marginBottom: 16,
        border: '1px solid #52c41a',
        backgroundColor: '#f6ffed'
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontStyle: 'italic', fontSize: '11px' }}>
          {source || 'Vitals Snapshot (Captured at Encounter Save Time)'} - {formattedVitals.length} vital signs
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: '10px' }}>
          These vital signs were automatically captured when the encounter was saved and become permanent when signed.
        </Text>
      </div>

      {formattedVitals.length > 0 ? (
        <Row gutter={[16, 16]}>
          {formattedVitals.map((vital, index) => (
            <Col key={index} xs={24} sm={12} md={8} lg={6}>
              {formatSnapshotVital(vital.label, {
                value: vital.value,
                units: vital.unit,
                timestamp: vital.timestamp
              })}
            </Col>
          ))}
        </Row>
      ) : (
        <Text type="secondary">No vital signs data in snapshot</Text>
      )}
    </Card>
  );
};

export default VitalsSnapshotDisplay;
